/* Centralized business data and JSON-LD schema helpers for SEO.
   These are consumed by JsonLd component and rendered into <head> via portals.
*/

export const BUSINESS = {
  name: "TERAFIQUIRO Wellness Center",
  legalName: "TERAFIQUIRO Wellness Center",
  slogan: "Al cuidado de tu cuerpo",
  url: "https://terafiquirocenter.com",
  logo: "https://terafiquirocenter.com/logo.png",
  image: "https://terafiquirocenter.com/img/specialties/10_huesero_quiropractica.png",
  telephone: "+52-771-214-1634",
  email: "citas@terafiquirocenter.com",
  priceRange: "$$",
  // Set ENABLED to true once you have real Google reviews to display.
  // Update ratingValue (1-5) and reviewCount with REAL aggregated numbers.
  // Schema.org requires honest values — fake reviews violate Google guidelines.
  aggregateRating: {
    enabled: false,
    ratingValue: 4.9,
    reviewCount: 0,
    bestRating: 5,
    worstRating: 1,
  },
  address: {
    streetAddress: "Calle El Pípila 200",
    addressLocality: "Pachuca de Soto",
    addressRegion: "Hidalgo",
    postalCode: "42000",
    addressCountry: "MX",
    neighborhood: "Colonia Centro",
  },
  geo: { latitude: 20.1011, longitude: -98.7591 },
  openingHours: [
    { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "20:00" },
    { days: ["Saturday"], opens: "09:00", closes: "14:00" },
  ],
  sameAs: [
    "https://www.facebook.com/terafiquirocenter",
    "https://www.instagram.com/terafiquirocenter",
  ],
  medicalSpecialties: [
    "Physiotherapy",
    "Chiropractic",
    "Sports Medicine",
    "Functional Medicine",
    "Ozone Therapy",
    "Aesthetic Medicine",
  ],
};

const baseAddress = () => ({
  "@type": "PostalAddress",
  streetAddress: BUSINESS.address.streetAddress,
  addressLocality: BUSINESS.address.addressLocality,
  addressRegion: BUSINESS.address.addressRegion,
  postalCode: BUSINESS.address.postalCode,
  addressCountry: BUSINESS.address.addressCountry,
});

const baseGeo = () => ({
  "@type": "GeoCoordinates",
  latitude: BUSINESS.geo.latitude,
  longitude: BUSINESS.geo.longitude,
});

const baseOpeningHours = () =>
  BUSINESS.openingHours.flatMap((b) =>
    b.days.map((day) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: `https://schema.org/${day}`,
      opens: b.opens,
      closes: b.closes,
    }))
  );

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["MedicalBusiness", "LocalBusiness"],
  "@id": `${BUSINESS.url}/#business`,
  name: BUSINESS.name,
  legalName: BUSINESS.legalName,
  slogan: BUSINESS.slogan,
  url: BUSINESS.url,
  logo: BUSINESS.logo,
  image: BUSINESS.image,
  telephone: BUSINESS.telephone,
  email: BUSINESS.email,
  priceRange: BUSINESS.priceRange,
  currenciesAccepted: "MXN",
  paymentAccepted: "Cash, Credit Card, Debit Card, Bank Transfer",
  address: baseAddress(),
  geo: baseGeo(),
  openingHoursSpecification: baseOpeningHours(),
  sameAs: BUSINESS.sameAs,
  medicalSpecialty: BUSINESS.medicalSpecialties,
  areaServed: [
    { "@type": "City", name: "Pachuca de Soto" },
    { "@type": "City", name: "Mineral de la Reforma" },
    { "@type": "State", name: "Hidalgo" },
  ],
  hasMap: "https://www.google.com/maps?q=Calle%20El%20Pipila%20200%20Colonia%20Centro%20Pachuca%20Hidalgo",
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BUSINESS.url}/#website`,
  url: BUSINESS.url,
  name: BUSINESS.name,
  inLanguage: ["es-MX", "en"],
  publisher: { "@id": `${BUSINESS.url}/#business` },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

export const servicePageSchema = (page) => {
  const url = `${BUSINESS.url}/servicios/${page.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "@id": `${url}#procedure`,
    name: page.title,
    description: page.metaDesc,
    bodyLocation: "Cuerpo humano",
    procedureType: "https://schema.org/TherapeuticProcedure",
    url,
    image: `${BUSINESS.url}${page.cover}`,
    provider: { "@id": `${BUSINESS.url}/#business` },
    availableService: page.techniques.map((t) => ({
      "@type": "MedicalTherapy",
      name: t,
    })),
    indication: page.conditions.map((c) => ({
      "@type": "MedicalIndication",
      name: c,
    })),
  };
};

export const articleSchema = (post, url) => ({
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  "@id": `${url}#article`,
  headline: post.title,
  description: post.excerpt,
  image: `${BUSINESS.url}${post.cover}`,
  datePublished: post.date,
  dateModified: post.date,
  inLanguage: "es-MX",
  author: { "@type": "Person", name: post.author },
  publisher: { "@id": `${BUSINESS.url}/#business` },
  mainEntityOfPage: { "@type": "WebPage", "@id": url },
  keywords: (post.tags || []).join(", "),
});

export const faqSchema = (qa) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qa.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

/**
 * Returns an AggregateRating fragment for the LocalBusiness schema,
 * but ONLY if BUSINESS.aggregateRating.enabled === true AND reviewCount > 0.
 * Otherwise returns null (Google rich-results requires honest, real reviews).
 */
export const aggregateRatingFragment = () => {
  const r = BUSINESS.aggregateRating;
  if (!r || !r.enabled || !r.reviewCount || r.reviewCount < 1) return null;
  return {
    "@type": "AggregateRating",
    ratingValue: r.ratingValue,
    reviewCount: r.reviewCount,
    bestRating: r.bestRating || 5,
    worstRating: r.worstRating || 1,
  };
};

/**
 * Optional override schema that AUGMENTS the base business JSON-LD with reviews.
 * Inject this on the landing once BUSINESS.aggregateRating.enabled is true.
 */
export const businessRatingSchema = () => {
  const rating = aggregateRatingFragment();
  if (!rating) return null;
  return {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "LocalBusiness"],
    "@id": `${BUSINESS.url}/#business`,
    name: BUSINESS.name,
    url: BUSINESS.url,
    aggregateRating: rating,
  };
};
